
import { HttpResponse } from '@angular/common/http';
import { SampleData, Sample, SampleMeta } from './../../samples/model/sample-management.model';
import { Injectable } from '@angular/core';
import _ from 'lodash';
import {
    SampleSet,
    MarshalledData,
    AnnotatedSampleDataEntry,
    SampleProperty,
    SampleSetMetaData
} from '../../samples/model/sample-management.model';
import {
    PutSamplesXLSXResponseDTO,
    ZomoPlanFileData
} from '../model/response.model';
import { ClientError } from '../model/client-error';
import {
    AnnotatedSampleDataDTO,
    AnnotatedSampleDataEntryDTO,
    SampleSetMetaDTO,
    SampleSetDTO,
    SampleDTO,
    SampleMetaDTO
} from '../model/shared-dto.model';
import { Urgency, NRL } from '../../samples/model/sample.enums';
import { toNrl } from '../../samples/model/nrl';

@Injectable({
    providedIn: 'root'
})
export class EntityFactoryService {

    toSample(dto: SampleDTO): Sample {
        return {
            sampleData: this.toSampleData(dto.sampleData),
            sampleMeta: dto.sampleMeta ? this.toSampleMeta(dto.sampleMeta) : {
                nrl: NRL.UNKNOWN,
                analysis: {},
                urgency: Urgency.NORMAL
            }
        };
    }

    toSampleSet(dto: SampleSetDTO): SampleSet {
        const annotatedSampleSet: SampleSet = {
            meta: this.toSampleSetMetaData(dto.meta),
            samples: dto.samples.map(sample => this.toSample(sample))
        };
        return annotatedSampleSet;
    }

    fromPutSamplesXLSXResponseDTOToMarshalledData(dto: PutSamplesXLSXResponseDTO): MarshalledData {
        return {
            binaryData: dto.data,
            mimeType: dto.type,
            fileName: dto.fileName
        };
    }

    fromBlobResponseToZomoPlanFileData(response: HttpResponse<Blob>, year: string): ZomoPlanFileData {
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = `ZomoPlan_${year}.docx`; // fallback filename

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^\n;=]*=((["']).*?\2|[^\n;]*)/);
            if (filenameMatch && filenameMatch[1]) {
                fileName = decodeURIComponent(
                    filenameMatch[1].replace(/["']/g, '')
                );
            }
        }

        const zomoPlanFileData: ZomoPlanFileData = {
            blob: response.body as Blob,
            fileName: fileName
        };

        return zomoPlanFileData;
    }

    private toSampleData(dto: AnnotatedSampleDataDTO): SampleData {
        const annotatedSampleData: Partial<SampleData> = {};
        (Object.keys(dto) as SampleProperty[]).forEach(prop => {
            const entry = dto[prop];
            if (entry) {
                annotatedSampleData[prop] =
                    this.toAnnotatedSampleDataEntry(entry);
            }
        });
        return annotatedSampleData as SampleData;
    }

    private toSampleMeta(dto: SampleMetaDTO): SampleMeta {
        return {
            nrl: dto.nrl ? toNrl(dto.nrl) : NRL.UNKNOWN,
            analysis: dto.analysis,
            urgency: dto.urgency ? this.fromUrgencyStringToEnum(dto.urgency) : Urgency.NORMAL
        };
    }

    private toSampleSetMetaData(dto: SampleSetMetaDTO): SampleSetMetaData {
        return {
            sender: dto.sender,
            fileName: dto.fileName ? dto.fileName : '',
            customerRefNumber: dto.customerRefNumber ? dto.customerRefNumber : '',
            signatureDate: dto.signatureDate ? dto.signatureDate : '',
            version: dto.version ? dto.version : ''
        };
    }

    private fromUrgencyStringToEnum(str: string): Urgency {
        switch (str.trim().toLowerCase()) {
            case 'eilt':
                return Urgency.URGENT;
            case 'normal':
            default:
                return Urgency.NORMAL;
        }
    }

    private toAnnotatedSampleDataEntry(dto: AnnotatedSampleDataEntryDTO): AnnotatedSampleDataEntry {
        try {
            const annotatedSampleDataEntry: AnnotatedSampleDataEntry = {
                value: dto.value,
                errors: dto.errors ? dto.errors : [],
                correctionOffer: dto.correctionOffer ? dto.correctionOffer : []
            };

            if (!_.isNil(dto.oldValue)) {
                annotatedSampleDataEntry.oldValue = dto.oldValue;
            }

            return annotatedSampleDataEntry;
        } catch (error) {
            throw new ClientError(
                `Error parsing input. error=${error}`
            );
        }
    }
}
